import { Injectable, inject } from '@angular/core';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { FIRESTORE_TOKEN } from '../firebase/firestore.provider';
import type { Sensor, SensorHistory, Site } from '../../shared/interfaces';

@Injectable({ providedIn: 'root' })
export class DataExtractService {
  private readonly firestore = inject(FIRESTORE_TOKEN);

  async fetchSensorHistories(sensorIds: string[], from: Date, to: Date): Promise<Map<string, SensorHistory[]>> {
    const historiesMap = new Map<string, SensorHistory[]>();
    const fromDateStr = this.formatDateString(from);
    const toDateStr = this.formatDateString(to);

    await Promise.all(sensorIds.map(async (sensorId) => {
      const historyRef = collection(this.firestore, `sensors/${sensorId}/history`);
      const dateHistoryQuery = query(
        historyRef,
        where('date', '>=', fromDateStr),
        where('date', '<=', toDateStr),
        orderBy('date', 'desc')
      );
      const measuredAtHistoryQuery = query(
        historyRef,
        where('measuredAt', '>=', from),
        where('measuredAt', '<=', to),
        orderBy('measuredAt', 'desc')
      );
      
      const [dateSnapshot, measuredAtSnapshot] = await Promise.all([
        getDocs(dateHistoryQuery),
        getDocs(measuredAtHistoryQuery)
      ]);
      const historiesById = new Map<string, SensorHistory>();
      for (const doc of [...dateSnapshot.docs, ...measuredAtSnapshot.docs]) {
        historiesById.set(doc.id, {
          id: doc.id,
          ...doc.data()
        } as SensorHistory);
      }
      
      historiesMap.set(sensorId, Array.from(historiesById.values()));
    }));

    return historiesMap;
  }

  mapToExportRows(site: Site, sensor: Sensor, histories: SensorHistory[]): string[][] {
    return histories.map(history => {
      const measuredAtStr = this.formatHistoryDate(history);
      
      return [
        site.id,
        site.name,
        sensor.id,
        sensor.categoryName,
        measuredAtStr,
        this.str(this.field(history, 'wbgt')),
        this.str(this.field(history, 'temperature')),
        this.str(this.field(history, 'humidity')),
        this.str(this.field(history, 'windSpeed') ?? this.field(history, 'speed')),
        this.str(this.field(history, 'windDirection') ?? this.field(history, 'direction')),
        this.str(this.field(history, 'noiseMax')),
        this.str(this.field(history, 'noiseL5')),
        this.str(this.field(history, 'vibrationMax')),
        this.str(this.field(history, 'vibrationL10')),
        this.str(this.field(history, 'ph')),
        this.str(this.field(history, 'tiltX')),
        this.str(this.field(history, 'tiltY')),
        this.str(this.field(history, 'tiltZ')),
        this.str(this.field(history, 'displacement')),
        this.str(this.field(history, 'battery') ?? this.field(history, 'batteryLevel')),
        '', // 通信状態
        ''  // 備考
      ];
    });
  }

  private field(history: SensorHistory, fieldName: string): unknown {
    const flat = (history as unknown as Record<string, unknown>)[fieldName];
    if (flat !== undefined) return flat;
    return history.data?.[fieldName];
  }

  private str(val: unknown): string {
    if (val === null || val === undefined) return '';
    return String(val);
  }

  private formatHistoryDate(history: SensorHistory): string {
    if (history.date) {
      return history.date.replaceAll('-', '/');
    }
    return history.measuredAt ? this.formatDate(history.measuredAt.toDate()) : '';
  }

  private formatDate(date: Date): string {
    const yyyy = date.getFullYear();
    const MM = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const HH = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    const ss = String(date.getSeconds()).padStart(2, '0');
    return `${yyyy}/${MM}/${dd} ${HH}:${mm}:${ss}`;
  }

  /**
   * Date オブジェクトを MQTT 互換の "YYYY-MM-DD HH:mm:ss" 形式文字列に変換する。
   * タイムゾーンは Asia/Tokyo (JST) を使用する。
   */
  private formatDateString(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const jst = new Date(date.getTime() + 9 * 60 * 60 * 1000);
    return `${jst.getUTCFullYear()}-${pad(jst.getUTCMonth() + 1)}-${pad(jst.getUTCDate())} ${pad(jst.getUTCHours())}:${pad(jst.getUTCMinutes())}:${pad(jst.getUTCSeconds())}`;
  }
}
